import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Button, Card, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography,
    Paper, InputAdornment, Tooltip, Avatar,
    Checkbox, FormControlLabel, FormGroup, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import GroupIcon from '@mui/icons-material/Group';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { TokenContext } from '../Context/TokenContext';
import { UserContext } from '../Context/UserContext';
import { PERMISSION_SECTIONS } from '../constants/permissions';

const API_BASE = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const emptyForm = { nom: '', prenom: '', email: '', password: '' };

function initials(nom, prenom) {
    return ((prenom?.[0] || '') + (nom?.[0] || '')).toUpperCase();
}

export default function EmployesAdminPage() {
    const { accessToken } = useContext(TokenContext);
    const { userInfo } = useContext(UserContext);
    const isSuperAdmin = userInfo?.is_super_admin === true;
    const userPermissions = userInfo?.permissions || [];
    const canManage = isSuperAdmin || userPermissions.includes('employes.manage');
    const canManageGroups = isSuperAdmin || userPermissions.includes('manage-own-group-members');

    const [employes, setEmployes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [search, setSearch] = useState('');

    const [groupes, setGroupes] = useState([]);
    const [applications, setApplications] = useState([]);

    const [openDialog, setOpenDialog] = useState(false);
    const [editingEmploye, setEditingEmploye] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [groupsTarget, setGroupsTarget] = useState(null);
    const [groupsSelection, setGroupsSelection] = useState([]);
    const [groupsSaving, setGroupsSaving] = useState(false);
    const [groupsError, setGroupsError] = useState('');

    const [permTarget, setPermTarget] = useState(null);
    const [permBaseline, setPermBaseline] = useState([]);
    const [permCurrent, setPermCurrent] = useState([]);
    const [permLoading, setPermLoading] = useState(false);
    const [permSaving, setPermSaving] = useState(false);
    const [permError, setPermError] = useState('');

    const headers = { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };

    const loadEmployes = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const r = await fetch(`${API_BASE}/api/admins`, { headers });
            const data = await r.json().catch(() => null);
            if (r.ok) {
                setEmployes((data || []).filter(a => !a.is_super_admin));
            } else {
                setLoadError(data?.error || `Erreur lors du chargement des employés (${r.status}).`);
            }
        } catch (e) {
            setLoadError('Erreur réseau lors du chargement des employés.');
        } finally {
            setLoading(false);
        }
    };

    const loadGroupes = async () => {
        try {
            const r = await fetch(`${API_BASE}/api/groupes`, { headers });
            if (r.ok) setGroupes(await r.json());
        } catch (e) {
            console.error('Erreur chargement groupes:', e);
        }
    };

    const loadApplications = async () => {
        try {
            const r = await fetch(`${API_BASE}/api/applications`, { headers });
            if (r.ok) setApplications(await r.json());
        } catch (e) {
            console.error('Erreur chargement applications:', e);
        }
    };

    useEffect(() => {
        loadEmployes();
        loadGroupes();
        loadApplications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Création / édition / suppression du compte ──────────────────────────────
    const openCreate = () => {
        setEditingEmploye(null);
        setForm(emptyForm);
        setFormError('');
        setShowPassword(false);
        setOpenDialog(true);
    };

    const openEdit = (employe) => {
        setEditingEmploye(employe);
        setForm({ nom: employe.nom, prenom: employe.prenom, email: employe.email, password: '' });
        setFormError('');
        setShowPassword(false);
        setOpenDialog(true);
    };

    const handleSave = async () => {
        if (!form.nom.trim() || !form.prenom.trim() || !form.email.trim()) {
            setFormError('Nom, prénom et email sont obligatoires.');
            return;
        }
        if (!editingEmploye && !form.password.trim()) {
            setFormError('Le mot de passe est obligatoire lors de la création.');
            return;
        }

        setSaving(true);
        setFormError('');
        try {
            const body = { nom: form.nom, prenom: form.prenom, email: form.email };
            if (form.password.trim()) body.password = form.password;

            const url = editingEmploye
                ? `${API_BASE}/api/admins/${editingEmploye.id}`
                : `${API_BASE}/api/admins`;
            const method = editingEmploye ? 'PUT' : 'POST';

            const r = await fetch(url, { method, headers, body: JSON.stringify(body) });
            const data = await r.json();
            if (!r.ok) { setFormError(data.error || 'Erreur lors de la sauvegarde.'); return; }

            setOpenDialog(false);
            loadEmployes();
        } catch (e) {
            setFormError('Erreur réseau.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await fetch(`${API_BASE}/api/admins/${deleteTarget.id}`, { method: 'DELETE', headers });
            setDeleteTarget(null);
            loadEmployes();
        } catch (e) {
            console.error('Erreur suppression:', e);
        } finally {
            setDeleting(false);
        }
    };

    // ── Gestion des groupes ──────────────────────────────────────────────────────
    const openGroups = (employe) => {
        setGroupsTarget(employe);
        setGroupsSelection((employe.groupes || []).map(g => g.id_groupe));
        setGroupsError('');
    };

    const toggleGroup = (id_groupe) => {
        setGroupsSelection(sel => sel.includes(id_groupe) ? sel.filter(id => id !== id_groupe) : [...sel, id_groupe]);
    };

    const handleSaveGroups = async () => {
        if (!groupsTarget) return;
        setGroupsSaving(true);
        setGroupsError('');
        try {
            const before = new Set((groupsTarget.groupes || []).map(g => g.id_groupe));
            const after = new Set(groupsSelection);
            const toAdd = [...after].filter(id => !before.has(id));
            const toRemove = [...before].filter(id => !after.has(id));

            for (const id_groupe of toAdd) {
                const r = await fetch(`${API_BASE}/api/groupes/${id_groupe}/membres`, {
                    method: 'POST', headers, body: JSON.stringify({ id_employe: groupsTarget.id })
                });
                if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Erreur ajout groupe'); }
            }
            for (const id_groupe of toRemove) {
                const r = await fetch(`${API_BASE}/api/groupes/${id_groupe}/membres/${groupsTarget.id}`, { method: 'DELETE', headers });
                if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Erreur retrait groupe'); }
            }

            setGroupsTarget(null);
            loadEmployes();
            loadGroupes();
        } catch (e) {
            setGroupsError(e.message || 'Erreur réseau.');
        } finally {
            setGroupsSaving(false);
        }
    };

    // ── Ajustement des permissions individuelles ─────────────────────────────────
    // baseline = ce que l'employé aurait via ses seuls groupes (déjà calculé côté serveur pour
    // chaque groupe, simple union ici) ; current = ses permissions effectives réelles (groupes +
    // surcharges). La différence entre les deux, au moment de l'enregistrement, redevient les
    // surcharges à stocker (grant si ajouté, revoke si retiré par rapport à la baseline).
    const openPermissions = async (employe) => {
        setPermTarget(employe);
        setPermError('');
        setPermLoading(true);
        try {
            const r = await fetch(`${API_BASE}/api/admins/${employe.id}/permissions`, { headers });
            const data = await r.json();
            if (!r.ok) { setPermError(data.error || 'Erreur lors du chargement des permissions.'); return; }

            const groupIds = (data.groupes || []).map(g => g.id_groupe);
            const baseline = new Set();
            for (const gid of groupIds) {
                const g = groupes.find(x => x.id_groupe === gid);
                (g?.effective_permissions || []).forEach(k => baseline.add(k));
            }
            setPermBaseline([...baseline]);
            setPermCurrent(data.effective_permissions || data.permissions || []);
        } catch (e) {
            setPermError('Erreur réseau.');
        } finally {
            setPermLoading(false);
        }
    };

    const togglePermission = (key) => {
        setPermCurrent(cur => cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key]);
    };

    const handleSavePermissions = async () => {
        if (!permTarget) return;
        setPermSaving(true);
        setPermError('');
        try {
            const baselineSet = new Set(permBaseline);
            const currentSet = new Set(permCurrent);
            const universe = new Set([...baselineSet, ...currentSet]);
            const overrides = [];
            for (const key of universe) {
                const inBaseline = baselineSet.has(key);
                const inCurrent = currentSet.has(key);
                if (inCurrent && !inBaseline) overrides.push({ permission_key: key, effect: 'grant' });
                else if (!inCurrent && inBaseline) overrides.push({ permission_key: key, effect: 'revoke' });
            }

            const r = await fetch(`${API_BASE}/api/admins/${permTarget.id}/permission-overrides`, {
                method: 'PUT', headers, body: JSON.stringify({ overrides })
            });
            const data = await r.json();
            if (!r.ok) { setPermError(data.error || 'Erreur lors de la sauvegarde.'); return; }
            setPermTarget(null);
            loadEmployes();
        } catch (e) {
            setPermError('Erreur réseau.');
        } finally {
            setPermSaving(false);
        }
    };

    const filtered = employes.filter(e => {
        const q = search.toLowerCase();
        return (
            e.nom?.toLowerCase().includes(q) ||
            e.prenom?.toLowerCase().includes(q) ||
            e.email?.toLowerCase().includes(q)
        );
    });

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2} mb={3}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <BadgeOutlinedIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>Employés</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Comptes backoffice à droits limités — permissions gérées par groupe ou individuellement
                        </Typography>
                    </Box>
                </Stack>
                {canManage && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreate}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}
                    >
                        Nouvel employé
                    </Button>
                )}
            </Stack>

            {loadError && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>{loadError}</Typography>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" mb={2} spacing={2}>
                <TextField
                    size="small"
                    placeholder="Rechercher un employé..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    sx={{ minWidth: { xs: '100%', sm: 280 } }}
                />
                <Chip
                    label={`${filtered.length} employé${filtered.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ bgcolor: 'primary.main', color: '#fff', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                />
            </Stack>

            <Card elevation={2}>
                <TableContainer component={Paper} elevation={0}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell width={56}></TableCell>
                                <TableCell><strong>Nom</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                                <TableCell><strong>Groupes</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={28} sx={{ color: 'primary.main' }} />
                                    </TableCell>
                                </TableRow>
                            ) : filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        {search ? 'Aucun résultat pour cette recherche.' : 'Aucun employé enregistré.'}
                                    </TableCell>
                                </TableRow>
                            ) : filtered.map(employe => (
                                <TableRow key={employe.id} hover>
                                    <TableCell>
                                        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 14 }}>
                                            {initials(employe.nom, employe.prenom)}
                                        </Avatar>
                                    </TableCell>
                                    <TableCell>
                                        <strong>{employe.prenom} {employe.nom}</strong>
                                    </TableCell>
                                    <TableCell>{employe.email}</TableCell>
                                    <TableCell>
                                        {(employe.groupes || []).length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                        ) : (
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {employe.groupes.map(g => (
                                                    <Chip key={g.id_groupe} label={g.nom} size="small" variant="outlined" />
                                                ))}
                                            </Stack>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {canManageGroups && (
                                            <Tooltip title="Gérer ses groupes">
                                                <IconButton size="small" onClick={() => openGroups(employe)}>
                                                    <GroupIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {isSuperAdmin && (
                                            <Tooltip title="Ajuster ses permissions individuelles">
                                                <IconButton size="small" onClick={() => openPermissions(employe)}>
                                                    <VpnKeyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {canManage && (
                                            <>
                                                <Tooltip title="Modifier">
                                                    <IconButton size="small" onClick={() => openEdit(employe)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Supprimer">
                                                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(employe)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {/* Dialog création / édition */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingEmploye ? "Modifier l'employé" : 'Créer un employé'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        {formError && <Typography color="error" variant="body2">{formError}</Typography>}
                        <Stack direction="row" spacing={2}>
                            <TextField label="Prénom" value={form.prenom} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))} fullWidth required autoFocus />
                            <TextField label="Nom" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} fullWidth required />
                        </Stack>
                        <TextField label="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} fullWidth required />
                        <TextField
                            label={editingEmploye ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe'}
                            type={showPassword ? 'text' : 'password'}
                            value={form.password}
                            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                            fullWidth
                            required={!editingEmploye}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} disabled={saving}>Annuler</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : (editingEmploye ? 'Enregistrer' : 'Créer')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog confirmation suppression */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Supprimer l'employé</DialogTitle>
                <DialogContent>
                    <Typography>
                        Voulez-vous supprimer le compte de <strong>{deleteTarget?.prenom} {deleteTarget?.nom}</strong> ?
                        Cette action est irréversible.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Annuler</Button>
                    <Button onClick={handleDelete} variant="contained" color="error" disabled={deleting}>
                        {deleting ? <CircularProgress size={20} color="inherit" /> : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog gestion des groupes */}
            <Dialog open={!!groupsTarget} onClose={() => setGroupsTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Groupes de {groupsTarget?.prenom} {groupsTarget?.nom}</DialogTitle>
                <DialogContent>
                    <Stack spacing={1} mt={1}>
                        {groupsError && <Typography color="error" variant="body2">{groupsError}</Typography>}
                        {groupes.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">Aucun groupe n'existe encore.</Typography>
                        ) : (
                            <FormGroup>
                                {groupes.map(g => (
                                    <FormControlLabel
                                        key={g.id_groupe}
                                        control={<Checkbox checked={groupsSelection.includes(g.id_groupe)} onChange={() => toggleGroup(g.id_groupe)} />}
                                        label={g.nom}
                                    />
                                ))}
                            </FormGroup>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGroupsTarget(null)} disabled={groupsSaving}>Annuler</Button>
                    <Button onClick={handleSaveGroups} variant="contained" disabled={groupsSaving} sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {groupsSaving ? <CircularProgress size={20} color="inherit" /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog ajustement des permissions individuelles */}
            <Dialog open={!!permTarget} onClose={() => setPermTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Permissions individuelles de {permTarget?.prenom} {permTarget?.nom}</DialogTitle>
                <DialogContent>
                    {permLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
                        </Box>
                    ) : (
                        <Stack spacing={2} mt={1}>
                            {permError && <Typography color="error" variant="body2">{permError}</Typography>}
                            <Typography variant="body2" color="text.secondary">
                                Coché par défaut = obtenu via les groupes de cet employé. Décocher retire explicitement le
                                droit ; cocher une case non héritée l'ajoute explicitement, indépendamment de ses groupes.
                            </Typography>
                            <Divider />
                            {PERMISSION_SECTIONS.map(sec => {
                                const sectionOptions = [
                                    ...(sec.pageKey ? [{ key: sec.pageKey, label: `Accès à la page ${sec.label}` }] : []),
                                    ...sec.actions,
                                ];
                                if (sectionOptions.length === 0) return null;
                                return (
                                    <Box key={sec.section} sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'uppercase' }}>
                                            {sec.label}
                                        </Typography>
                                        <FormGroup>
                                            {sectionOptions.map(({ key, label }) => {
                                                const inherited = permBaseline.includes(key);
                                                const checked = permCurrent.includes(key);
                                                return (
                                                    <FormControlLabel
                                                        key={key}
                                                        control={<Checkbox checked={checked} onChange={() => togglePermission(key)} />}
                                                        label={
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <span>{label}</span>
                                                                {inherited !== checked && (
                                                                    <Chip
                                                                        label={checked ? 'ajouté' : 'retiré'}
                                                                        size="small"
                                                                        color={checked ? 'success' : 'error'}
                                                                        variant="outlined"
                                                                    />
                                                                )}
                                                                {inherited && checked && (
                                                                    <Chip label="hérité" size="small" variant="outlined" />
                                                                )}
                                                            </Stack>
                                                        }
                                                    />
                                                );
                                            })}
                                        </FormGroup>
                                    </Box>
                                );
                            })}
                            {applications.filter(a => a.is_internal !== true).length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'uppercase' }}>
                                        Applications externes
                                    </Typography>
                                    <FormGroup>
                                        {applications.filter(a => a.is_internal !== true).map(a => {
                                            const key = `app:${a.client_id}`;
                                            const label = a.name || a.client_id;
                                            const inherited = permBaseline.includes(key);
                                            const checked = permCurrent.includes(key);
                                            return (
                                                <FormControlLabel
                                                    key={key}
                                                    control={<Checkbox checked={checked} onChange={() => togglePermission(key)} />}
                                                    label={
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <span>{label}</span>
                                                            {inherited !== checked && (
                                                                <Chip label={checked ? 'ajouté' : 'retiré'} size="small" color={checked ? 'success' : 'error'} variant="outlined" />
                                                            )}
                                                            {inherited && checked && <Chip label="hérité" size="small" variant="outlined" />}
                                                        </Stack>
                                                    }
                                                />
                                            );
                                        })}
                                    </FormGroup>
                                </Box>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPermTarget(null)} disabled={permSaving}>Annuler</Button>
                    <Button onClick={handleSavePermissions} variant="contained" disabled={permSaving || permLoading} sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {permSaving ? <CircularProgress size={20} color="inherit" /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
